import { useState } from "react";
import { useNavigate } from "react-router-dom";
import BoringAvatar from "boring-avatars";
import { Copy, MoreHorizontal, Trash2 } from "lucide-react";
import { mutate } from "swr";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import useToast from "~/components/ui/toast";
import { cn } from "~/lib/utils";
import { ROUTE_NEXTGEN_VIEW } from "~/router/constants";
import {
  AGENT_BRAND_COLORS,
  Agent,
  AgentScope,
  agentListPath,
  deleteAgent,
  duplicateAgent,
} from "~/services/agents";
import { StatusBadge } from "./StatusBadge";

interface AgentCardProps {
  agent: Agent;
  canManage: boolean;
}

function formatUpdatedAt(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export const AgentCard = ({ agent, canManage }: AgentCardProps) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const accentColor =
    agent.scope === AgentScope.SYSTEM
      ? "var(--agent-accent-system)"
      : "var(--agent-accent-customer)";

  const handleDuplicate = async () => {
    try {
      await duplicateAgent(agent.id);
      await mutate(agentListPath());
      showSuccess(`"${agent.display_name}" created`);
    } catch {
      showError("Failed to duplicate agent");
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteAgent(agent.id);
      await mutate(agentListPath());
      showSuccess(`"${agent.display_name}" deleted`);
    } catch {
      showError("Failed to delete agent");
    } finally {
      setIsDeleting(false);
      setDeleteOpen(false);
    }
  };

  return (
    <>
      <Card
        className={cn(
          "group h-full gap-0 overflow-hidden p-0",
          "cursor-pointer transition-colors hover:border-border/80 hover:bg-accent/40"
        )}
        onClick={() => navigate(`${ROUTE_NEXTGEN_VIEW}/agents/${agent.id}`)}
      >
        <div className="h-2 w-full shrink-0" style={{ backgroundColor: accentColor }} />
        <div className="flex flex-1 flex-col gap-3 p-5">
          <div className="flex items-center gap-3">
            <div className="size-11 shrink-0 overflow-hidden rounded-full">
              <BoringAvatar
                size={44}
                name={agent.name}
                variant="beam"
                colors={AGENT_BRAND_COLORS}
              />
            </div>
            <div className="min-w-0 flex-1">
              <span className="block text-base font-semibold text-card-foreground leading-tight group-hover:text-foreground">
                {agent.display_name}
              </span>
            </div>
            {canManage && (
              <div onClick={(e) => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        "size-7 shrink-0 text-muted-foreground",
                        "opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100",
                        "focus-visible:ring-0 focus-visible:border-transparent"
                      )}
                      aria-label={`Actions for ${agent.display_name}`}
                    >
                      <MoreHorizontal />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleDuplicate}>
                      <Copy />
                      Duplicate
                    </DropdownMenuItem>
                    {agent.scope !== AgentScope.SYSTEM && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem variant="destructive" onClick={() => setDeleteOpen(true)}>
                          <Trash2 />
                          Delete
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>

          <p className="line-clamp-3 flex-1 text-sm text-muted-foreground">{agent.description}</p>

          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-1.5">
              <StatusBadge status={agent.status} />
              {agent.scope === AgentScope.SYSTEM && (
                <Badge variant="outline" className="rounded-full">
                  System
                </Badge>
              )}
            </div>
            <p className="shrink-0 text-xs text-muted-foreground/60">
              Updated {formatUpdatedAt(agent.updated_at)}
            </p>
          </div>
        </div>
      </Card>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{agent.display_name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? "Deleting…" : "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
